import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  Consultation,
  ConsultationSellingAdviceCard,
  ConsultationSellingAdviceFinding,
  ConsultationSellingFinding,
  ConsultationSellingReview,
  ConsultationSellingWentWell,
  SellingDiscoveryState,
  rerunSellingReviewAsync,
  fetchSellingReviewJobAsync
} from '../store/slices/consultationsSlice';
import { getApiErrorMessage } from '../utils/apiError';
import AiBadge from './AiBadge';
import {
  concernLabel,
  decisionOutcomeLabel,
  constraintLabel,
  discoveryItemLabel,
  discoveryStateLabel,
  fitLabel,
  habitLabel,
  handlingLabel,
  mistakeLabel,
  strengthLabel,
  wantLabel
} from './sellingReviewLabels';
import './ConsultationSellingReviewSection.css';

interface Props {
  consultation: Consultation;
  onRefresh: () => void | Promise<void>;
}

const POLL_INTERVAL_MS = 15_000;
const STALE_PROCESSING_MS = 45 * 60 * 1000;
const PLAY_DURATION_MS = 8_000;
const API_BASE_URL = import.meta.env.VITE_API_URL as string;

function formatMs(ms: number | null): string {
  if (ms == null) return '';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const DISCOVERY_ITEMS: Array<keyof ConsultationSellingReview['discovery']> = [
  'goal',
  'pastExperience',
  'constraints',
  'decisionCriteria'
];

const ConsultationSellingReviewSection: React.FC<Props> = ({ consultation, onRefresh }) => {
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((state) => state.auth);

  const job = consultation.sellingReviewJob || null;
  const review = consultation.sellingReview || null;
  const recording = consultation.recording || null;

  // A job "processing" for over 45 min lost its worker; the API accepts a re-run.
  const [loadedAt] = useState(() => Date.now());
  const staleProcessing = !!(job && job.status === 'processing' && job.startedAt
    && loadedAt - new Date(job.startedAt).getTime() > STALE_PROCESSING_MS);
  const isRunning = !!(job && (job.status === 'pending' || job.status === 'processing')) && !staleProcessing;
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Poll the job endpoint while pending/processing, same cadence as insights.
  // On a terminal state, refetch the consultation to pick up the new review.
  useEffect(() => {
    if (!isRunning) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(async () => {
      const latest = await dispatch(fetchSellingReviewJobAsync(consultation.id)).unwrap().catch(() => null);
      if (latest && latest.status !== 'pending' && latest.status !== 'processing') {
        await onRefresh();
      }
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isRunning, consultation.id, dispatch, onRefresh]);

  const stale = !!(review && review.transcriptionJobId !== recording?.activeJobId);
  const unhealthyWarning = !!(review && recording?.healthy === false);

  // ---- Audio mini-player (reuses the recordings/:id/audio-url approach from
  // ConsultationAIInsightsSection). Each playable quote gets a string key so
  // only one button shows the "playing" state at a time. ----
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ensureAudioUrl = useCallback(async (): Promise<string | null> => {
    if (audioUrl) return audioUrl;
    if (!recording) return null;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/recordings/${recording.id}/audio-url`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setAudioUrl(res.data.url);
      return res.data.url;
    } catch {
      return null;
    }
  }, [audioUrl, recording, token]);

  const stopSegment = useCallback(() => {
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
    if (audioRef.current) audioRef.current.pause();
    setPlayingKey(null);
  }, []);

  const playSegment = useCallback(async (key: string, startMs: number) => {
    const url = await ensureAudioUrl();
    if (!url || !audioRef.current) return;
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
    if (audioRef.current.src !== url) audioRef.current.src = url;
    audioRef.current.currentTime = startMs / 1000;
    audioRef.current.play().catch(() => { /* ignore */ });
    setPlayingKey(key);
    pauseTimerRef.current = setTimeout(() => {
      if (audioRef.current) audioRef.current.pause();
      setPlayingKey(null);
    }, PLAY_DURATION_MS);
  }, [ensureAudioUrl]);

  useEffect(() => () => {
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
  }, []);

  // ---- Re-run ----
  const [rerunning, setRerunning] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const canRerun = !!consultation.aiInsightsJob && recording?.healthy !== false && !!recording?.activeJobId && !isRunning;

  const handleRerun = useCallback(async () => {
    setRerunning(true);
    setRerunError(null);
    try {
      await dispatch(rerunSellingReviewAsync(consultation.id)).unwrap();
      await onRefresh();
    } catch (err) {
      setRerunError(typeof err === 'string' ? err : getApiErrorMessage(err, 'Failed to start review job'));
    } finally {
      setRerunning(false);
    }
  }, [consultation.id, dispatch, onRefresh]);

  const rerunTitle = !consultation.aiInsightsJob
    ? 'Generate AI insights first to lock speakers.'
    : recording?.healthy === false
      ? 'Recording is flagged unhealthy.'
      : !recording?.activeJobId
        ? 'Transcribe a recording first.'
        : '';

  const close = useCallback(() => {
    setOpen(false);
    stopSegment();
  }, [stopSegment]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const runTitle = rerunError
    || (job && job.status === 'failed' && job.lastError ? `Last run failed: ${job.lastError}` : '')
    || (staleProcessing ? 'Last run failed: the worker stopped mid-run. Re-run the review.' : '')
    || rerunTitle;

  const metaParts = review
    ? [
        review.promptVersion ? `prompt ${review.promptVersion}` : 'prompt —',
        review.completedAt ? new Date(review.completedAt).toLocaleString() : null
      ].filter(Boolean)
    : [];

  return (
    <>
      <div className="selling-review-trigger">
        {review ? (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setOpen(true)}
          >
            View review
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary"
            onClick={handleRerun}
            disabled={!canRerun || rerunning || isRunning}
            title={runTitle}
          >
            {isRunning ? 'Processing…' : 'Run review'}
          </button>
        )}
        {isRunning && review && (
          <span className="selling-review-trigger__status">Processing…</span>
        )}
      </div>

      {open && createPortal(
        <div className="selling-review-dialog-overlay">
          <div
            className="selling-review selling-review-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="selling-review-dialog-title"
          >
            <div className="selling-review-dialog__top">
              <div className="selling-review-dialog__heading">
                <h2 id="selling-review-dialog-title" className="selling-review__title">
                  Consultation review
                  <AiBadge title="Generated by AI. Read-only." />
                </h2>
                {metaParts.length > 0 && (
                  <div className="selling-review__meta">{metaParts.join(', ')}</div>
                )}
              </div>
              <div className="selling-review-dialog__actions">
                <button
                  type="button"
                  className="selling-review-dialog__rerun"
                  onClick={handleRerun}
                  disabled={!canRerun || rerunning}
                  title={rerunTitle}
                >
                  {isRunning ? 'Processing…' : 'Re-run review'}
                </button>
                <button
                  type="button"
                  className="selling-review-dialog__close"
                  onClick={close}
                  aria-label="Close review"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="selling-review-dialog__body">
              {rerunError && (
                <div className="selling-review__error">{rerunError}</div>
              )}

              {job && job.status === 'failed' && job.lastError && (
                <div className="selling-review__banner selling-review__banner--fail">
                  <strong>Last run failed:</strong> {job.lastError}
                </div>
              )}

              {staleProcessing && (
                <div className="selling-review__banner selling-review__banner--fail">
                  <strong>Last run failed:</strong> the worker stopped mid-run. Re-run the review.
                </div>
              )}

              {stale && (
                <div className="selling-review__banner selling-review__banner--warn">
                  Transcript was re-run after this review — re-run the review to refresh.
                </div>
              )}

              {unhealthyWarning && (
                <div className="selling-review__banner selling-review__banner--warn">
                  This recording is flagged unhealthy — the review may be unreliable.
                </div>
              )}

              {isRunning && (
                <p className="selling-review__status">
                  {review ? 'A new review is processing…' : 'Processing…'}
                </p>
              )}

              {review && (
                <div className="selling-review__page">
                  <div className="selling-review-lead">
                    <section className="selling-review-verdict">
                      <DecisionLine review={review} />
                      <p className="selling-review-verdict__fit">
                        <span className="selling-review-verdict__fit-label">{fitLabel(review.fit)}</span>
                        {review.fitReason && (
                          <>
                            {': '}
                            <span className="selling-review-verdict__fit-reason">{review.fitReason}</span>
                          </>
                        )}
                      </p>
                    </section>

                    {review.headline && (
                      <p className="selling-review__headline">{review.headline}</p>
                    )}
                    {review.summary && (
                      <p className="selling-review__summary">{review.summary}</p>
                    )}
                  </div>

                  <section className="selling-review-section">
                    <SectionHead title="What we learned" lede="What the conversation made clear about the client. For information, not a checklist." />
                    <DiscoveryScorecard review={review} />
                  </section>

                  {review.advice.length > 0 && (
                    <section className="selling-review-section">
                      <SectionHead title="Next time" lede="What to change on your next consultation." />
                      {review.advice.map((card, i) => (
                        <AdviceCardView
                          key={i}
                          card={card}
                          cardIndex={i}
                          playingKey={playingKey}
                          onPlay={playSegment}
                          onStop={stopSegment}
                        />
                      ))}
                    </section>
                  )}

                  {review.wentWell && (
                    <section className="selling-review-section">
                      <SectionHead title="Went well" lede="Keep doing this." />
                      <WentWellCardView
                        wentWell={review.wentWell}
                        playingKey={playingKey}
                        onPlay={playSegment}
                        onStop={stopSegment}
                      />
                    </section>
                  )}

                  <section className="selling-review-section">
                    <SectionHead title="Findings" lede="Every moment the review is based on, with a link to the recording." />
                    <FindingsPanel
                      review={review}
                      playingKey={playingKey}
                      onPlay={playSegment}
                      onStop={stopSegment}
                    />
                  </section>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <audio
        ref={audioRef}
        style={{ display: 'none' }}
        controls={false}
        onEnded={() => setPlayingKey(null)}
        onPause={() => setPlayingKey(null)}
      />
    </>
  );
};

// =============================================================================
// PlayButton — small inline play/stop button for a timestamped quote.
// =============================================================================

const PlayButton: React.FC<{
  playKey: string;
  startMs: number;
  playingKey: string | null;
  onPlay: (key: string, startMs: number) => void;
  onStop: () => void;
}> = ({ playKey, startMs, playingKey, onPlay, onStop }) => {
  const isPlaying = playingKey === playKey;
  return (
    <button
      type="button"
      className={`selling-review__play${isPlaying ? ' is-playing' : ''}`}
      onClick={() => (isPlaying ? onStop() : onPlay(playKey, startMs))}
      title={isPlaying ? 'Stop playback' : 'Play this moment'}
    >
      {isPlaying ? '■ Stop' : `▶ ${formatMs(startMs)}`}
    </button>
  );
};

const QuoteBlock: React.FC<{
  quote: string;
  playKey: string;
  startMs: number | null;
  playingKey: string | null;
  onPlay: (key: string, startMs: number) => void;
  onStop: () => void;
}> = ({ quote, playKey, startMs, playingKey, onPlay, onStop }) => (
  <div className="selling-review__quote">
    <p className="selling-review__quote-text">&ldquo;{quote}&rdquo;</p>
    {startMs != null && (
      <PlayButton
        playKey={playKey}
        startMs={startMs}
        playingKey={playingKey}
        onPlay={onPlay}
        onStop={onStop}
      />
    )}
  </div>
);

// =============================================================================
// DecisionPill / FitPill
// =============================================================================

const DecisionLine: React.FC<{ review: ConsultationSellingReview }> = ({ review }) => {
  const clear = review.decisionOutcome !== 'unclear';
  const label = decisionOutcomeLabel(review.decisionOutcome);
  const text = review.decisionDetail ? `${label}: ${review.decisionDetail}` : label;
  return (
    <p className={`selling-review-verdict__decision ${clear ? 'selling-review-verdict__decision--ok' : 'selling-review-verdict__decision--alert'}`}>
      {text}
    </p>
  );
};

// =============================================================================
// AdviceCardView
// =============================================================================

const AdviceCardView: React.FC<{
  card: ConsultationSellingAdviceCard;
  cardIndex: number;
  playingKey: string | null;
  onPlay: (key: string, startMs: number) => void;
  onStop: () => void;
}> = ({ card, cardIndex, playingKey, onPlay, onStop }) => (
  <article className="selling-review__note">
    <h4 className="selling-review__note-title">{card.title}</h4>
    <p className="selling-review__habit">{habitLabel(card.habit)}</p>

    <p className="selling-review__prose">{card.whatHappened}</p>
    {card.findings.map((f: ConsultationSellingAdviceFinding, i: number) => (
      <QuoteBlock
        key={i}
        quote={f.quote}
        playKey={`advice-${cardIndex}-${i}`}
        startMs={f.startMs}
        playingKey={playingKey}
        onPlay={onPlay}
        onStop={onStop}
      />
    ))}

    <p className="selling-review__prose selling-review__prose--follow">{card.why}</p>

    <div className="selling-review__next">
      <p className="selling-review__next-label">Try next time</p>
      <ul className="selling-review__phrases">
        {card.tryNextTime.map((phrase, i) => (
          <li key={i}>{phrase}</li>
        ))}
      </ul>
      <p className="selling-review__hint">Examples, not a script</p>
    </div>
  </article>
);

// =============================================================================
// WentWellCardView
// =============================================================================

const WentWellCardView: React.FC<{
  wentWell: ConsultationSellingWentWell;
  playingKey: string | null;
  onPlay: (key: string, startMs: number) => void;
  onStop: () => void;
}> = ({ wentWell, playingKey, onPlay, onStop }) => (
  <div className="selling-review__went-well">
    <p className="selling-review__prose selling-review__prose--lead">{wentWell.text}</p>
    <QuoteBlock
      quote={wentWell.quote}
      playKey="went-well"
      startMs={wentWell.startMs}
      playingKey={playingKey}
      onPlay={onPlay}
      onStop={onStop}
    />
  </div>
);

// =============================================================================
// SectionHead — top-level section heading with a one-line description.
// =============================================================================

const SectionHead: React.FC<{ title: string; lede: string }> = ({ title, lede }) => (
  <header className="selling-review-section__head">
    <h3 className="selling-review-section__title">{title}</h3>
    <p className="selling-review-section__lede">{lede}</p>
  </header>
);

// =============================================================================
// DiscoveryScorecard — one cell per discovery item, a Harvey ball per state.
// =============================================================================

const HarveyBall: React.FC<{ state: SellingDiscoveryState }> = ({ state }) => (
  <svg className={`selling-review-ball selling-review-ball--${state}`} viewBox="0 0 32 32" aria-hidden="true">
    <circle cx="16" cy="16" r="14" className="selling-review-ball__ring" />
    {state === 'covered' && <circle cx="16" cy="16" r="10" className="selling-review-ball__fill" />}
    {state === 'partial' && <path d="M16 6 A10 10 0 0 1 16 26 Z" className="selling-review-ball__fill" />}
    {state === 'not_needed' && <path d="M9 16 H23" className="selling-review-ball__ring" />}
  </svg>
);

const DiscoveryScorecard: React.FC<{ review: ConsultationSellingReview }> = ({ review }) => {
  const states = DISCOVERY_ITEMS.map((item) => review.discovery?.[item] || 'missing');
  const covered = states.filter((s) => s === 'covered').length;
  const relevant = states.filter((s) => s !== 'not_needed').length;

  return (
    <div className="selling-review-scorecard">
      <div className="selling-review-scorecard__head">
        <h4 className="selling-review-scorecard__title">Discovery</h4>
        <p className="selling-review-scorecard__tally">
          <strong>{covered}</strong> of {relevant} clear
        </p>
      </div>
      <ul className="selling-review-scorecard__grid">
        {DISCOVERY_ITEMS.map((item, i) => {
          const state = states[i];
          return (
            <li key={item} className={`selling-review-scorecard__cell selling-review-scorecard__cell--${state}`}>
              <HarveyBall state={state} />
              <span className="selling-review-scorecard__name">{discoveryItemLabel(item)}</span>
              <span className={`selling-review-scorecard__state selling-review__state--${state}`}>
                {discoveryStateLabel(state)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// =============================================================================
// FindingsPanel
// =============================================================================

const FindingsPanel: React.FC<{
  review: ConsultationSellingReview;
  playingKey: string | null;
  onPlay: (key: string, startMs: number) => void;
  onStop: () => void;
}> = ({ review, playingKey, onPlay, onStop }) => {
  const { findings } = review;

  return (
    <div className="selling-review-learned">
      <div className="selling-review-split">
        <div className="selling-review-split__col">
          <h4 className="selling-review__group-title">The client</h4>

          <h5 className="selling-review__group-title selling-review__group-title--sub">Wants</h5>
          {findings.wants.length === 0 && (
            <p className="selling-review__empty">None noted.</p>
          )}
          {findings.wants.map((w) => (
            <FindingRow key={w.id} finding={w} label={wantLabel(w.code)} playingKey={playingKey} onPlay={onPlay} onStop={onStop} />
          ))}

          <h5 className="selling-review__group-title selling-review__group-title--sub">Constraints</h5>
          {findings.constraints.length === 0 && (
            <p className="selling-review__empty">None noted.</p>
          )}
          {findings.constraints.map((c) => (
            <FindingRow key={c.id} finding={c} label={constraintLabel(c.code)} playingKey={playingKey} onPlay={onPlay} onStop={onStop} />
          ))}

          <h5 className="selling-review__group-title selling-review__group-title--sub">Concerns</h5>
          {findings.concerns.length === 0 && (
            <p className="selling-review__empty">None noted.</p>
          )}
          {findings.concerns.map((c) => (
            <FindingRow key={c.id} finding={c} label={concernLabel(c.code)} playingKey={playingKey} onPlay={onPlay} onStop={onStop}>
              {c.handling && (
                <span className={`selling-review__mark selling-review__state--${handlingStateClass(c.handling)}`}>
                  {handlingLabel(c.handling)}
                </span>
              )}
            </FindingRow>
          ))}
        </div>

        <div className="selling-review-split__col">
          <h4 className="selling-review__group-title">How you ran it</h4>

          <h5 className="selling-review__group-title selling-review__group-title--sub">Mistakes</h5>
          {findings.mistakes.length === 0 && (
            <p className="selling-review__empty">None noted.</p>
          )}
          {findings.mistakes.map((m) => (
            <FindingRow key={m.id} finding={m} label={mistakeLabel(m.code)} playingKey={playingKey} onPlay={onPlay} onStop={onStop}>
              {m.severity && (
                <span className={`selling-review__mark selling-review__state--${m.severity === 'major' ? 'missing' : 'partial'}`}>
                  {m.severity}
                </span>
              )}
            </FindingRow>
          ))}

          <h5 className="selling-review__group-title selling-review__group-title--sub">Strengths</h5>
          {findings.strengths.length === 0 && (
            <p className="selling-review__empty">None noted.</p>
          )}
          {findings.strengths.map((s) => (
            <FindingRow key={s.id} finding={s} label={strengthLabel(s.code)} playingKey={playingKey} onPlay={onPlay} onStop={onStop} />
          ))}
        </div>
      </div>
    </div>
  );
};

function handlingStateClass(handling: string): string {
  if (handling === 'handled') return 'covered';
  if (handling === 'badly_answered') return 'partial';
  return 'missing';
}

const FindingRow: React.FC<{
  finding: ConsultationSellingFinding;
  label: string;
  playingKey: string | null;
  onPlay: (key: string, startMs: number) => void;
  onStop: () => void;
  children?: React.ReactNode;
}> = ({ finding, label, playingKey, onPlay, onStop, children }) => (
  <div className="selling-review__finding">
    <div className="selling-review__finding-head">
      <span className="selling-review__finding-name">{label}</span>
      {children}
    </div>
    <QuoteBlock
      quote={finding.quote}
      playKey={`finding-${finding.id}`}
      startMs={finding.startMs}
      playingKey={playingKey}
      onPlay={onPlay}
      onStop={onStop}
    />
    {finding.explanation && (
      <p className="selling-review__finding-note">{finding.explanation}</p>
    )}
    {finding.consequence && (
      <p className="selling-review__finding-note selling-review__finding-note--effect">
        <strong>Effect:</strong> {finding.consequence}
      </p>
    )}
  </div>
);

export default ConsultationSellingReviewSection;
